import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import {
  CHAT_MESSAGE_MAX_LENGTH,
  clampChatMessage,
  normalizeOutgoingChatMessage,
} from '@core/gameengine/chat/chatMessageLimits';
import {
  isOwnChatMessage,
  trackOwnChatPayload,
  trackOwnChatSend,
} from '@core/gameengine/chat/chatMessageOwnership';
import { useChat, type ChatMessage } from '@core/gameengine/state/chat';
import { GlobalBus } from '@core/Bus/EventBus';
import { useAuth } from '@core/gameengine/state/auth';
import {
  SKYX_NEW_CLOSE_CHAT,
  SKYX_NEW_CLOSE_MENU,
} from '@core/lobby/skyxNewPanelEvents';
import { useActiveGameStore } from '@core/gameengine/state/activeGameStore';
import { getGameFromUrl } from '@core/utils/urlParser';
import { isSkyxNewRoute } from '@core/utils/gameRouteSlugs';
import { formatSkyxPlayerDisplayName } from '@core/utils/skyxPlayerDisplayName';
import { useT, formatChatMessageTime } from '@core/i18n';
import { parseSkyxWinShareMessage } from '@core/utils/skyxChatWinShare';
import {
  SkyxNewChatWinCard,
  formatShareDate,
} from '@core/lobby/skyx/SkyxNewChatWinCard';
import { SkyxNewChatRulesModal } from '@core/lobby/skyx/SkyxNewChatRulesModal';
import { SkyxNewModalCloseButton } from '@core/UI/skyx/SkyxNewModalCloseButton';
import {
  getSkyxNewChatPanelAnchoredStyle,
  getSkyxNewChatPanelLayout,
  getSkyxNewEmojiPickerGridMaxHeightPx,
  getSkyxNewHandheldLandscapeEmojiPickerOverlayHeightPx,
  getSkyxNewHandheldLandscapeEmojiPickerPositionStyle,
  getSkyxNewHandheldLandscapeEmojiPickerChrome,
  isSkyxNewChatUsesStageBoardAnchor,
  isSkyxNewChatCompactChrome,
  isSkyxNewChatFullCanvasBleed,
  isSkyxNewChatPortaledToStageBoard,
  readSkyxChatStageBoardAnchor,
  skyxNewChatHeaderLayoutStyles,
  type SkyxChatStageBoardAnchor,
} from '@core/lobby/skyxNewChatWidgetStyles';
import { isSkyxHandheldLandscape } from '@core/utils/skyxViewport';
import { SKYX_NEW_NS } from '@core/utils/skyxNewI18n';
import {
  ensureServerAvatarPool,
  resolvePlayerAvatarUrl,
} from '@core/utils/skyxPlayerAvatar';
import { SKYX_NEW_PLAYER_AVATAR_CHANGED } from '@core/lobby/skyxNewPanelEvents';

const SKYX_CHAT_EMOJIS = [
  { emoji: '😀', name: 'grinning face happy smile' },
  { emoji: '😃', name: 'smiling face happy joy' },
  { emoji: '😄', name: 'grinning smile laugh' },
  { emoji: '😁', name: 'beaming smile happy' },
  { emoji: '😆', name: 'grin laugh funny' },
  { emoji: '😅', name: 'sweat smile relief' },
  { emoji: '😂', name: 'tears of joy laugh funny' },
  { emoji: '🤣', name: 'rolling laughing floor' },
  { emoji: '😊', name: 'blush smile shy' },
  { emoji: '😇', name: 'angel halo innocent' },
  { emoji: '🙂', name: 'slight smile' },
  { emoji: '🙃', name: 'upside down silly' },
  { emoji: '😉', name: 'wink playful' },
  { emoji: '😌', name: 'relieved calm' },
  { emoji: '😍', name: 'heart eyes love' },
  { emoji: '🥰', name: 'smiling hearts love' },
  { emoji: '😘', name: 'kiss love' },
  { emoji: '😗', name: 'kiss face' },
  { emoji: '😙', name: 'smiling kiss' },
  { emoji: '😚', name: 'closed eyes kiss' },
  { emoji: '😋', name: 'yum tongue tasty' },
  { emoji: '😛', name: 'tongue playful' },
  { emoji: '😝', name: 'squint tongue silly' },
  { emoji: '😜', name: 'wink tongue joke' },
  { emoji: '🤪', name: 'crazy goofy' },
  { emoji: '🤨', name: 'raised eyebrow suspicious' },
  { emoji: '🧐', name: 'monocle curious' },
  { emoji: '🤓', name: 'nerd glasses' },
  { emoji: '😎', name: 'cool sunglasses' },
  { emoji: '🥳', name: 'party celebration' },
  { emoji: '🤩', name: 'star struck wow' },
  { emoji: '😏', name: 'smirk' },
  { emoji: '😒', name: 'unamused annoyed' },
  { emoji: '😞', name: 'disappointed sad' },
  { emoji: '😔', name: 'pensive sad' },
  { emoji: '😟', name: 'worried' },
  { emoji: '😕', name: 'confused' },
  { emoji: '🙁', name: 'slightly frowning' },
  { emoji: '☹️', name: 'frowning sad' },
  { emoji: '😣', name: 'persevering struggle' },
  { emoji: '😖', name: 'confounded upset' },
  { emoji: '😫', name: 'tired upset' },
  { emoji: '😩', name: 'weary tired' },
  { emoji: '🥺', name: 'pleading puppy eyes' },
  { emoji: '😢', name: 'cry sad tear' },
  { emoji: '😭', name: 'sob loudly cry' },
  { emoji: '😤', name: 'triumph steam nose' },
  { emoji: '😠', name: 'angry mad' },
  { emoji: '😡', name: 'pouting furious rage' },
  { emoji: '🤬', name: 'swearing censored rage' },
  { emoji: '🤯', name: 'mind blown shock' },
  { emoji: '😳', name: 'flushed embarrassed' },
  { emoji: '🥵', name: 'hot face' },
  { emoji: '🥶', name: 'cold face' },
  { emoji: '😱', name: 'scream fear shocked' },
  { emoji: '😨', name: 'fearful scared' },
  { emoji: '😰', name: 'anxious sweat' },
  { emoji: '😥', name: 'sad relieved' },
  { emoji: '😓', name: 'downcast sweat' },
  { emoji: '🤗', name: 'hug embrace' },
  { emoji: '🤔', name: 'thinking' },
  { emoji: '🫡', name: 'salute respect' },
  { emoji: '🤭', name: 'hand over mouth' },
  { emoji: '🫢', name: 'open eyes hand mouth' },
  { emoji: '🫣', name: 'peeking shy' },
  { emoji: '🤫', name: 'shh quiet' },
  { emoji: '🤥', name: 'liar long nose' },
  { emoji: '😶', name: 'no mouth silent' },
  { emoji: '🫠', name: 'melting face' },
  { emoji: '🙄', name: 'rolling eyes' },
  { emoji: '😬', name: 'grimace awkward' },
  { emoji: '😮', name: 'open mouth surprise' },
  { emoji: '😯', name: 'hushed surprise' },
  { emoji: '😲', name: 'astonished' },
  { emoji: '😴', name: 'sleeping sleepy' },
  { emoji: '🤤', name: 'drooling' },
  { emoji: '😪', name: 'sleepy' },
  { emoji: '😵', name: 'dizzy knocked out' },
  { emoji: '🫨', name: 'shaking shocked' },
  { emoji: '🤐', name: 'zipper mouth secret' },
  { emoji: '🥴', name: 'woozy drunk' },
  { emoji: '🤢', name: 'nauseated sick' },
  { emoji: '🤮', name: 'vomiting sick' },
  { emoji: '🤧', name: 'sneeze sick' },
  { emoji: '😷', name: 'mask sick' },
  { emoji: '🤒', name: 'thermometer sick' },
  { emoji: '🤕', name: 'bandage hurt' },
  { emoji: '❤️', name: 'red heart love' },
  { emoji: '🩷', name: 'pink heart love' },
  { emoji: '🧡', name: 'orange heart' },
  { emoji: '💛', name: 'yellow heart' },
  { emoji: '💚', name: 'green heart' },
  { emoji: '💙', name: 'blue heart' },
  { emoji: '💜', name: 'purple heart' },
  { emoji: '🖤', name: 'black heart' },
  { emoji: '🤍', name: 'white heart' },
  { emoji: '🤎', name: 'brown heart' },
  { emoji: '💔', name: 'broken heart' },
  { emoji: '❣️', name: 'heart exclamation' },
  { emoji: '💕', name: 'two hearts' },
  { emoji: '💞', name: 'revolving hearts' },
  { emoji: '💓', name: 'beating heart' },
  { emoji: '💗', name: 'growing heart' },
  { emoji: '💖', name: 'sparkling heart' },
  { emoji: '💘', name: 'heart arrow' },
  { emoji: '💝', name: 'heart ribbon gift' },
  { emoji: '💯', name: 'hundred perfect' },
  { emoji: '🔥', name: 'fire lit' },
  { emoji: '✨', name: 'sparkles magic' },
  { emoji: '⭐', name: 'star' },
  { emoji: '🌟', name: 'glowing star' },
  { emoji: '💫', name: 'dizzy star' },
  { emoji: '⚡', name: 'lightning fast' },
  { emoji: '💥', name: 'boom explosion' },
  { emoji: '☄️', name: 'comet' },
  { emoji: '🎉', name: 'party popper celebration' },
  { emoji: '🎊', name: 'confetti ball' },
  { emoji: '🎈', name: 'balloon party' },
  { emoji: '🎂', name: 'birthday cake' },
  { emoji: '🍾', name: 'champagne celebration' },
  { emoji: '🥂', name: 'cheers glasses' },
  { emoji: '🍻', name: 'beer cheers' },
  { emoji: '🙌', name: 'raised hands celebrate' },
  { emoji: '👏', name: 'clap applause' },
  { emoji: '👍', name: 'thumbs up like yes' },
  { emoji: '👎', name: 'thumbs down no' },
  { emoji: '👊', name: 'fist bump punch' },
  { emoji: '✊', name: 'raised fist power' },
  { emoji: '🤛', name: 'left fist' },
  { emoji: '🤜', name: 'right fist' },
  { emoji: '👌', name: 'ok hand' },
  { emoji: '✌️', name: 'victory peace' },
  { emoji: '🤞', name: 'crossed fingers luck' },
  { emoji: '🤟', name: 'love you hand' },
  { emoji: '🤘', name: 'rock on' },
  { emoji: '🤙', name: 'call me hand' },
  { emoji: '👋', name: 'wave hello bye' },
  { emoji: '🫶', name: 'heart hands' },
  { emoji: '🙏', name: 'pray thanks please' },
  { emoji: '💪', name: 'muscle strong' },
  { emoji: '🫵', name: 'pointing at you' },
  { emoji: '👀', name: 'eyes looking' },
  { emoji: '🧠', name: 'brain smart' },
  { emoji: '👑', name: 'crown king queen' },
  { emoji: '💎', name: 'diamond gem' },
  { emoji: '💰', name: 'money bag cash' },
  { emoji: '💸', name: 'money flying' },
  { emoji: '🤑', name: 'money face rich' },
  { emoji: '🎮', name: 'video game controller' },
  { emoji: '🎯', name: 'target bullseye' },
  { emoji: '🏆', name: 'trophy win' },
  { emoji: '🥇', name: 'gold medal first' },
  { emoji: '🚀', name: 'rocket launch' },
  { emoji: '✈️', name: 'airplane flight' },
  { emoji: '🌈', name: 'rainbow' },
  { emoji: '☀️', name: 'sun sunny' },
  { emoji: '🌙', name: 'moon night' },
] as const;

const SKYX_NEW_CHAT_CHROME = {
  headerFooterBg: '#170550',
  accentText: '#9e54e3',
  messageInputBg: '#271574',
  sendButtonBg: '#34cf72',
  /** Other players' display name above their message */
  otherUsernameText: '#ffffff',
  /** "you" label above your own message */
  youLabelText: '#ffd3c1',
  /** Other users' message bubble text */
  otherMessageText: '#ffd3c1',
  /** Current user's message bubble text */
  ownMessageText: '#fff6da',
  timestampText: '#ffffff',
} as const;

/** Above #gamehud (z-index 3) and bet panels — skyx_new / sky-x only. */
const SKYX_NEW_CHAT_PANEL_Z_INDEX = 10050;

const isConnectionOverlayActive = (status: string): boolean =>
  status !== 'connected' && status !== 'idle';

const ChatRulesMenuIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
    style={{ display: 'block' }}
  >
    <circle cx="12" cy="7" r="1.75" />
    <rect x="10.75" y="10.5" width="2.5" height="9" rx="1.25" />
  </svg>
);

const ChatEmojiIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
    <line x1="9" y1="10" x2="9.01" y2="10" />
    <line x1="15" y1="10" x2="15.01" y2="10" />
  </svg>
);

const ChatSendIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
    style={{ display: 'block' }}
  >
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const ChatHeartIcon = ({ filled }: { filled: boolean }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    style={{ display: 'block' }}
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const useDeviceType = (): 'mobile' | 'tablet' | 'desktop' =>
  useActiveGameStore(state => state.deviceType);

export const ChatWidget: React.FC = () => {
  const t = useT();
  const tSkyx = useT(SKYX_NEW_NS);
  const deviceType = useDeviceType();
  const isLandscape = useActiveGameStore(state => state.isLandscape);
  const connectionStatus = useActiveGameStore(
    state => state.connectionStatus as string
  );
  const [gameName, setGameName] = useState<string | null>(() =>
    getGameFromUrl()
  );
  const isSkyxNew = isSkyxNewRoute(gameName);
  const [isOpen, setIsOpen] = useState(false);
  const [isChatRulesOpen, setIsChatRulesOpen] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState('');
  const [avatarRevision, setAvatarRevision] = useState(0);
  const [inputText, setInputText] = useState('');
  const [chatPopupMessage, setChatPopupMessage] = useState<string | null>(null);
  const [showNewMessageIndicator, setShowNewMessageIndicator] = useState(false);
  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const sentMessageIds = useRef(new Set<string>());
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const chatPanelRef = useRef<HTMLDivElement>(null);
  /** Mobile portrait: keep the panel locked to the canvas rect while chat is open. */
  const frozenStageBoardAnchorRef = useRef<SkyxChatStageBoardAnchor | null>(
    null
  );

  const isAtBottomRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  const forceScrollToLatestRef = useRef(false);
  const user = useAuth(state => state.user);
  const userRef = useRef(user);
  userRef.current = user;
  const messages = useChat(state => state.messages);
  const error = useChat(state => state.error);
  const updateMessages = useChat(state => state.updateMessages);
  const addMessage = useChat(state => state.addMessage);
  const setError = useChat(state => state.setError);
  const updateMessageLikes = useChat(state => state.updateMessageLikes);
  const setChatOpen = useChat(state => state.setChatOpen);

  useEffect(() => {
    setChatOpen(isOpen);
  }, [isOpen, setChatOpen]);

  // Keep receiving live chat while the panel is closed so unread can light up.
  // Wait for STOMP — skyx_new uses SUBSCRIBE, which throws if called before connect.
  useEffect(() => {
    if (connectionStatus !== 'connected') return;

    const playgrid = (window as any).playgrid;
    const gameController = playgrid?.getCurrentGameController?.();
    if (gameController?.subscribeToChat) {
      gameController.subscribeToChat();
    }
  }, [connectionStatus]);

  useEffect(() => {
    if (!isOpen) {
      setIsChatRulesOpen(false);
      setIsEmojiPickerOpen(false);
      setEmojiSearch('');
      setShowNewMessageIndicator(false);
      isAtBottomRef.current = true;
      prevMessageCountRef.current = 0;
      forceScrollToLatestRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    const onNav = () => setGameName(getGameFromUrl());
    window.addEventListener('playgrid:navigation', onNav as EventListener);
    return () =>
      window.removeEventListener('playgrid:navigation', onNav as EventListener);
  }, []);

  useEffect(() => {
    if (isSkyxNew) {
      void ensureServerAvatarPool();
    }
  }, [isSkyxNew]);

  useEffect(() => {
    if (!isSkyxNew) return;
    return GlobalBus.on(
      'chat:like_update',
      (data: {
        clientSeq?: string;
        likeCount?: number;
        likedByMe?: boolean;
      }) => {
        if (!data?.clientSeq) return;
        const current = useChat
          .getState()
          .messages.find(message => message.clientSeq === data.clientSeq);
        if (!current) return;
        updateMessageLikes(
          data.clientSeq,
          typeof data.likeCount === 'number'
            ? data.likeCount
            : current.likeCount,
          typeof data.likedByMe === 'boolean'
            ? data.likedByMe
            : current.likedByMe
        );
      }
    );
  }, [isSkyxNew, updateMessageLikes]);

  useEffect(() => {
    if (!isSkyxNew) return;
    return GlobalBus.on(SKYX_NEW_PLAYER_AVATAR_CHANGED, () => {
      setAvatarRevision(value => value + 1);
    });
  }, [isSkyxNew]);

  useEffect(() => {
    if (!isSkyxNew || !isEmojiPickerOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (emojiPickerRef.current?.contains(target)) return;
      if (composerRef.current?.contains(target)) return;
      setIsEmojiPickerOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isEmojiPickerOpen, isSkyxNew]);

  // Reset emoji search whenever the picker closes (toggle, outside click, select, send).
  useEffect(() => {
    if (!isEmojiPickerOpen) {
      setEmojiSearch('');
    }
  }, [isEmojiPickerOpen]);

  // Scroll only the messages panel — never scrollIntoView.
  // In casino iframes (e.g. DAZN BET), scrollIntoView walks ancestors and can
  // scroll the parent page when chat opens or a message is sent.
  const scrollToLatest = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const el = messagesAreaRef.current;
    if (!el) return;
    if (behavior === 'smooth' && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  const updateScrollPositionState = useCallback(() => {
    const el = messagesAreaRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 48;
    isAtBottomRef.current = atBottom;
    if (atBottom) {
      setShowNewMessageIndicator(false);
    }
  }, []);

  const handleNewMessageIndicatorClick = useCallback(() => {
    isAtBottomRef.current = true;
    setShowNewMessageIndicator(false);
    scrollToLatest();
  }, [scrollToLatest]);

  const toggleChat = useCallback(() => {
    const newIsOpen = !isOpen;
    if (newIsOpen && isSkyxNew) {
      GlobalBus.emit(SKYX_NEW_CLOSE_MENU, {});
    }
    setIsOpen(newIsOpen);

    // Subscribe to chat when opening the chat widget
    if (newIsOpen) {
      const playgrid = (window as any).playgrid;
      const gameController = playgrid?.getCurrentGameController();
      if (gameController && gameController.subscribeToChat) {
        console.log('[ChatWidget] Opening chat, subscribing to chat...');
        gameController.subscribeToChat();
      }
    }
  }, [isOpen, isSkyxNew]);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;

      if (chatPanelRef.current?.contains(target)) return;
      if (emojiPickerRef.current?.contains(target)) return;

      const el = target instanceof Element ? target : target.parentElement;
      if (el?.closest('[data-chat-toggle]')) return;
      // Nested / portaled child modals (chat rules, PF/seed, share, etc.) —
      // closing them must not dismiss the chat parent.
      if (
        el?.closest(
          [
            '[data-skyx-chat-rules-overlay]',
            '[data-skyx-modal-backdrop]',
            '[data-skyx-modal-panel]',
            '[data-skyx-popup]',
            '[aria-modal="true"]',
          ].join(', ')
        )
      ) {
        return;
      }

      closeChat();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isOpen, closeChat]);

  useEffect(() => {
    if (!isOpen) return;
    if (isConnectionOverlayActive(connectionStatus)) {
      setIsOpen(false);
    }
  }, [connectionStatus, isOpen]);

  useEffect(() => {
    if (!isSkyxNew) return;
    return GlobalBus.on(SKYX_NEW_CLOSE_CHAT, () => setIsOpen(false));
  }, [isSkyxNew]);

  useEffect(() => {
    const syncOwnMessagesFromBatch = (batch: any[]) => {
      for (const message of batch) {
        trackOwnChatPayload(message, {
          user: userRef.current,
          sentClientSeqs: sentMessageIds.current,
          persistNickname: isSkyxNew,
        });
      }
    };

    const handleHistory = (data: any) => {
      if (data && data.messages && Array.isArray(data.messages)) {
        console.log(
          '[ChatWidget] Received chat update with',
          data.messages.length,
          'messages'
        );
        syncOwnMessagesFromBatch(data.messages);
        updateMessages(data.messages);
        if (!isSkyxNew) {
          setTimeout(() => {
            scrollToLatest();
          }, 100);
        }
      }
    };
    const handleAllMessages = (data: any) => {
      if (data && data.messages && Array.isArray(data.messages)) {
        console.log(
          '[ChatWidget] Received all messages with',
          data.messages.length,
          'messages'
        );
        syncOwnMessagesFromBatch(data.messages);
        updateMessages(data.messages);
      }
    };
    const handleNewMessage = (data: any) => {
      console.log('[ChatWidget] Received new message:', data?.meta?.clientSeq);
      const isOwnMessage = trackOwnChatPayload(data, {
        user: userRef.current,
        sentClientSeqs: sentMessageIds.current,
        persistNickname: isSkyxNew,
      });
      if (isOwnMessage) {
        console.log('[ChatWidget] This is our sent message!');
      }
      addMessage(data, { isOwn: isOwnMessage });
      if (isSkyxNew && isOwnMessage) {
        forceScrollToLatestRef.current = true;
      } else if (!isSkyxNew) {
        setTimeout(() => {
          scrollToLatest();
        }, 100);
      }
    };
    const handleError = (errorData: any) => {
      console.log('[ChatWidget] Received game:error event:', errorData);
      if (errorData?.code === 'CHAT_PROFANITY_BLOCKED') {
        if (isSkyxNew) {
          setChatPopupMessage(t('ui.chat.please_type_valid_message'));
        } else {
          setError(t('ui.chat.please_type_valid_message'));
        }
      } else if (
        errorData?.code === 'CHAT_MESSAGE_TOO_LONG' ||
        errorData?.code === 'CHAT_TOO_LONG'
      ) {
        setError(
          errorData.message ||
            t('ui.chat.message_too_long', { max: CHAT_MESSAGE_MAX_LENGTH })
        );
      }
    };

    const handleToggle = () => {
      toggleChat();
    };

    const handleSendMessageEvent = (data: {
      text?: string;
      open?: boolean;
    }) => {
      const messageText = normalizeOutgoingChatMessage(data?.text ?? '');
      if (!messageText) return;

      if (data?.open !== false) {
        if (isSkyxNew) {
          GlobalBus.emit(SKYX_NEW_CLOSE_MENU, {});
        }
        setIsOpen(true);
        const playgrid = (window as any).playgrid;
        const gameController = playgrid?.getCurrentGameController();
        if (gameController?.subscribeToChat) {
          gameController.subscribeToChat();
        }
      }

      const playgrid = (window as any).playgrid;
      const gameController = playgrid?.getCurrentGameController();
      if (gameController?.sendChatMessage) {
        const clientId = gameController.sendChatMessage(messageText);
        if (clientId) {
          trackOwnChatSend(clientId, user, sentMessageIds.current);
        }
      }
    };

    const unsubHistory = GlobalBus.on('chat:history', handleHistory);
    const unsubAll = GlobalBus.on('chat:all_messages', handleAllMessages);
    const unsubNew = GlobalBus.on('chat:new_message', handleNewMessage);
    const unsubError = GlobalBus.on('game:error', handleError);
    const unsubToggle = GlobalBus.on('chat:toggle', handleToggle);
    const unsubSend = GlobalBus.on('chat:send_message', handleSendMessageEvent);

    return () => {
      unsubHistory();
      unsubAll();
      unsubNew();
      unsubError();
      unsubToggle();
      unsubSend();
    };
  }, [
    updateMessages,
    addMessage,
    setError,
    toggleChat,
    isSkyxNew,
    scrollToLatest,
    t,
  ]);

  useEffect(() => {
    if (!isOpen) return;

    if (!isSkyxNew) {
      scrollToLatest();
      return;
    }

    const count = messages.length;
    const prev = prevMessageCountRef.current;

    if (count > prev) {
      const shouldAutoScroll =
        prev === 0 || isAtBottomRef.current || forceScrollToLatestRef.current;

      if (shouldAutoScroll) {
        forceScrollToLatestRef.current = false;
        setTimeout(() => {
          scrollToLatest();
          setShowNewMessageIndicator(false);
        }, 50);
      } else {
        setShowNewMessageIndicator(true);
      }
    }

    prevMessageCountRef.current = count;
  }, [messages, isOpen, isSkyxNew, scrollToLatest]);

  useEffect(() => {
    if (!chatPopupMessage) return;
    const timer = window.setTimeout(() => setChatPopupMessage(null), 2500);
    return () => window.clearTimeout(timer);
  }, [chatPopupMessage]);

  const handleSendMessage = (e?: React.KeyboardEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const playgrid = (window as any).playgrid;
    const gameController = playgrid?.getCurrentGameController();
    const messageText = normalizeOutgoingChatMessage(inputText);

    console.log('[ChatWidget] Attempting to send message:', {
      hasPlaygrid: !!playgrid,
      hasGameController: !!gameController,
      inputText: messageText,
      originalInputText: inputText,
    });

    if (!messageText) {
      if (isSkyxNew) {
        setChatPopupMessage(t('ui.chat.please_type_message'));
      } else {
        setError(t('ui.chat.please_type_message'));
      }
      return;
    }

    if (!gameController) {
      console.warn('[ChatWidget] Cannot send message: no game controller');
      return;
    }

    const clientId = gameController.sendChatMessage(messageText);
    if (!clientId) return;

    trackOwnChatSend(clientId, user, sentMessageIds.current);
    console.log('[ChatWidget] Sent message with clientId:', clientId);
    setInputText('');
    setIsEmojiPickerOpen(false);
  };

  const handleEmojiSelect = (emoji: string) => {
    setInputText(current => clampChatMessage(`${current}${emoji}`));
    setIsEmojiPickerOpen(false);
    requestAnimationFrame(() => {
      chatInputRef.current?.focus({ preventScroll: true });
    });
  };

  const handleInputChange = (value: string) => {
    setInputText(clampChatMessage(value));
    if (chatPopupMessage) {
      setChatPopupMessage(null);
    }
  };

  const handleToggleLike = useCallback(
    (message: ChatMessage) => {
      const playgrid = (window as any).playgrid;
      const gameController = playgrid?.getCurrentGameController();
      if (!gameController?.sendChatLike) return;

      const nextLiked = !message.likedByMe;
      const nextCount = Math.max(0, message.likeCount + (nextLiked ? 1 : -1));
      updateMessageLikes(message.clientSeq, nextCount, nextLiked);
      gameController.sendChatLike(message.clientSeq);
    },
    [updateMessageLikes]
  );

  const normalizedEmojiSearch = emojiSearch.trim().toLowerCase();
  const filteredEmojis = SKYX_CHAT_EMOJIS.filter(
    item => !normalizedEmojiSearch || item.name.includes(normalizedEmojiSearch)
  );

  const getPanelWidth = (): string => {
    switch (deviceType) {
      case 'mobile':
        return '100%';
      case 'tablet':
        return '50%';
      default:
        return '300px';
    }
  };

  const chatPortaledToStageBoard =
    isSkyxNew && isSkyxNewChatPortaledToStageBoard(deviceType, isLandscape);
  const chatUsesStageBoardAnchor =
    isSkyxNew && isSkyxNewChatUsesStageBoardAnchor(deviceType, isLandscape);
  const chatFullCanvasBleed =
    isSkyxNew && isSkyxNewChatFullCanvasBleed(deviceType, isLandscape);
  const chatHandheldLandscape =
    isSkyxNew && isSkyxHandheldLandscape(deviceType, isLandscape);
  const chatCompactChrome =
    isSkyxNew && isSkyxNewChatCompactChrome(deviceType, isLandscape);
  const [stageBoardAnchor, setStageBoardAnchor] =
    useState<SkyxChatStageBoardAnchor | null>(null);
  const [chatPortalRoot, setChatPortalRoot] = useState<HTMLElement | null>(
    null
  );

  useLayoutEffect(() => {
    if (!isSkyxNew || typeof document === 'undefined') {
      setChatPortalRoot(null);
      return;
    }
    setChatPortalRoot(document.body);
  }, [isSkyxNew]);

  useLayoutEffect(() => {
    if (!isSkyxNew || !chatUsesStageBoardAnchor) {
      frozenStageBoardAnchorRef.current = null;
      setStageBoardAnchor(null);
      return;
    }

    if (!isOpen) {
      frozenStageBoardAnchorRef.current = null;
      setStageBoardAnchor(null);
      return;
    }

    const commitAnchor = (anchor: SkyxChatStageBoardAnchor | null) => {
      if (!anchor) {
        setStageBoardAnchor(null);
        return;
      }
      if (chatFullCanvasBleed) {
        frozenStageBoardAnchorRef.current = anchor;
      }
      setStageBoardAnchor(anchor);
    };

    const updateAnchor = (forceRecapture = false) => {
      if (
        chatFullCanvasBleed &&
        frozenStageBoardAnchorRef.current &&
        !forceRecapture
      ) {
        setStageBoardAnchor(frozenStageBoardAnchorRef.current);
        return;
      }
      commitAnchor(readSkyxChatStageBoardAnchor());
    };

    const scheduleAnchorRefresh = (forceRecapture = false) => {
      updateAnchor(forceRecapture);
      requestAnimationFrame(() => updateAnchor(forceRecapture));
      for (const delayMs of [100, 300]) {
        window.setTimeout(() => updateAnchor(forceRecapture), delayMs);
      }
    };

    scheduleAnchorRefresh(true);

    const board = document.getElementById('stage-board');
    const resizeObserver =
      board && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            if (chatFullCanvasBleed && frozenStageBoardAnchorRef.current) {
              return;
            }
            scheduleAnchorRefresh(false);
          })
        : null;
    if (board && resizeObserver) {
      resizeObserver.observe(board);
    }

    const onWindowResize = () => {
      if (chatFullCanvasBleed) {
        frozenStageBoardAnchorRef.current = null;
      }
      scheduleAnchorRefresh(true);
    };

    const onOrientationChange = () => {
      frozenStageBoardAnchorRef.current = null;
      scheduleAnchorRefresh(true);
    };

    const onVisualViewportChange = () => {
      if (chatFullCanvasBleed && frozenStageBoardAnchorRef.current) {
        return;
      }
      scheduleAnchorRefresh(false);
    };

    const onScroll = () => {
      if (chatFullCanvasBleed && frozenStageBoardAnchorRef.current) {
        return;
      }
      updateAnchor(false);
    };

    window.addEventListener('resize', onWindowResize);
    window.addEventListener('orientationchange', onOrientationChange);
    window.addEventListener('fullscreenchange', onOrientationChange);
    window.visualViewport?.addEventListener('resize', onVisualViewportChange);
    window.addEventListener('scroll', onScroll, true);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', onWindowResize);
      window.removeEventListener('orientationchange', onOrientationChange);
      window.removeEventListener('fullscreenchange', onOrientationChange);
      window.visualViewport?.removeEventListener(
        'resize',
        onVisualViewportChange
      );
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [
    chatFullCanvasBleed,
    chatUsesStageBoardAnchor,
    deviceType,
    isLandscape,
    isOpen,
    isSkyxNew,
  ]);

  useEffect(() => {
    if (!isSkyxNew || typeof document === 'undefined') return;
    document.body.classList.toggle('skyx-new-chat-open', isOpen);
    return () => {
      document.body.classList.remove('skyx-new-chat-open');
    };
  }, [isOpen, isSkyxNew]);

  const skyxPanelLayout = isSkyxNew
    ? chatUsesStageBoardAnchor
      ? stageBoardAnchor
        ? getSkyxNewChatPanelAnchoredStyle(
            deviceType,
            isLandscape,
            stageBoardAnchor
          )
        : chatFullCanvasBleed
          ? null
          : getSkyxNewChatPanelLayout(deviceType, isLandscape)
      : getSkyxNewChatPanelLayout(deviceType, isLandscape)
    : null;
  const chatLayoutReady = !chatUsesStageBoardAnchor || stageBoardAnchor != null;
  const chatDesktopCanvasAnchored =
    isSkyxNew &&
    deviceType === 'desktop' &&
    chatUsesStageBoardAnchor &&
    stageBoardAnchor != null;

  // --- STYLES ---
  const chatPanelStyle: React.CSSProperties = {
    position: skyxPanelLayout?.position ?? 'fixed',
    bottom: skyxPanelLayout?.bottom ?? (deviceType === 'mobile' ? '0' : '20px'),
    top: skyxPanelLayout?.top,
    left: skyxPanelLayout?.left,
    right: skyxPanelLayout?.right ?? '0',
    margin: skyxPanelLayout?.margin,
    transform: chatFullCanvasBleed
      ? isOpen
        ? undefined
        : 'translateY(110%)'
      : chatPortaledToStageBoard
        ? isOpen
          ? undefined
          : 'translateX(100%)'
        : skyxPanelLayout?.transform
          ? isOpen
            ? skyxPanelLayout.transform
            : `${skyxPanelLayout.transform} translateY(110%)`
          : isOpen
            ? 'translateX(0)'
            : 'translateX(100%)',

    opacity: isOpen && chatLayoutReady ? 1 : 0,
    pointerEvents: isOpen && chatLayoutReady ? 'auto' : 'none',
    visibility: isOpen && !chatLayoutReady ? 'hidden' : undefined,

    width: skyxPanelLayout?.width ?? getPanelWidth(),
    maxWidth: skyxPanelLayout?.maxWidth,
    height:
      chatHandheldLandscape || chatDesktopCanvasAnchored
        ? undefined
        : (skyxPanelLayout?.height ??
          (deviceType === 'mobile'
            ? 'calc(70dvh - env(safe-area-inset-bottom, 0px))'
            : 'calc(70% - 70px)')),
    background: 'rgba(13, 17, 23, 0.4)',
    backdropFilter: 'blur(10px)',
    borderLeft: chatPortaledToStageBoard
      ? undefined
      : '1px solid rgba(255,255,255,0.1)',
    borderRadius:
      skyxPanelLayout?.borderRadius ??
      (deviceType === 'mobile' ? '0' : '8px 0 0 8px'),
    display: 'flex',
    flexDirection: 'column',
    zIndex: isSkyxNew ? SKYX_NEW_CHAT_PANEL_Z_INDEX : 2100,
    overflow: isSkyxNew ? 'hidden' : undefined,
    minHeight: isSkyxNew ? 0 : undefined,

    transition: `transform 0.3s ease-in-out, opacity 0.3s ease-in-out`,

    boxShadow: '0 0 15px rgba(0,0,0,0.5)',
    userSelect: 'none',
    paddingBottom:
      deviceType === 'mobile' && !chatPortaledToStageBoard
        ? 'env(safe-area-inset-bottom, 0px)'
        : undefined,
    boxSizing: 'border-box',
  };
  // --- MODIFIED SECTION END ---

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: chatCompactChrome ? '10px 12px' : '16px',
    fontSize: chatCompactChrome ? '16px' : '18px',
    fontWeight: '600',
    color: isSkyxNew ? SKYX_NEW_CHAT_CHROME.accentText : '#e8f0f8',
    background: isSkyxNew
      ? SKYX_NEW_CHAT_CHROME.headerFooterBg
      : 'rgba(13, 17, 23, 0.7)',
    userSelect: 'none',
    ...(isSkyxNew ? { flexShrink: 0 } : {}),
    ...(isSkyxNew ? skyxNewChatHeaderLayoutStyles.root : {}),
  };
  const headerTitleGroupStyle: React.CSSProperties = isSkyxNew
    ? skyxNewChatHeaderLayoutStyles.left
    : {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        minWidth: 0,
      };
  const headerCloseGroupStyle: React.CSSProperties = isSkyxNew
    ? skyxNewChatHeaderLayoutStyles.right
    : {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexShrink: 0,
      };
  const closeButtonStyle: React.CSSProperties = {
    ...(isSkyxNew ? skyxNewChatHeaderLayoutStyles.iconButton : {}),
    ...(isSkyxNew ? skyxNewChatHeaderLayoutStyles.closeButton : {}),
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    appearance: 'none',
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    color: isSkyxNew ? undefined : '#e8f0f8',
    fontSize: isSkyxNew ? '26px' : '24px',
    cursor: 'pointer',
    lineHeight: 1,
    padding: isSkyxNew ? 0 : undefined,
  };
  const messagesAreaContainerStyle: React.CSSProperties = {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    overflow: isSkyxNew ? 'hidden' : undefined,
    display: 'flex',
    flexDirection: 'column',
  };
  const messagesAreaStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    padding: '16px',
    paddingBottom:
      chatHandheldLandscape && isEmojiPickerOpen
        ? `${getSkyxNewHandheldLandscapeEmojiPickerOverlayHeightPx(deviceType, isLandscape)}px`
        : undefined,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  };
  const newMessageIndicatorStyle: React.CSSProperties = {
    position: 'absolute',
    left: '50%',
    bottom: '12px',
    transform: 'translateX(-50%)',
    background: SKYX_NEW_CHAT_CHROME.sendButtonBg,
    color: '#ffffff',
    border: 'none',
    borderRadius: '999px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.35)',
    zIndex: 2,
  };
  const skyxMessageRowStyle = (isYou: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: isYou ? 'flex-end' : 'flex-start',
    gap: '8px',
    width: '100%',
  });
  const skyxMessageContentStyle = (isYou: boolean): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: isYou ? 'flex-end' : 'flex-start',
    minWidth: 0,
    flex: 1,
    maxWidth: 'calc(100% - 24px)',
  });
  const messageStyle = (
    isYou: boolean,
    isWinShare: boolean
  ): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: isWinShare ? 'stretch' : isYou ? 'flex-end' : 'flex-start',
    color: isSkyxNew ? undefined : '#e8f0f8',
    width: isWinShare ? '100%' : undefined,
  });
  const skyxAvatarStyle: React.CSSProperties = {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
    border: '1px solid rgba(255,255,255,0.18)',
    background: '#271574',
    boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
  };
  const skyxAvatarFallbackStyle: React.CSSProperties = {
    ...skyxAvatarStyle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
  };
  const messageBubbleStyle = (isYou: boolean): React.CSSProperties => ({
    maxWidth: isSkyxNew ? '92%' : '80%',
    padding: '8px 12px',
    borderRadius: '12px',
    background: isYou ? '#22c55e' : 'rgba(43, 57, 69, 0.7)',
    color: isSkyxNew
      ? isYou
        ? SKYX_NEW_CHAT_CHROME.ownMessageText
        : SKYX_NEW_CHAT_CHROME.otherMessageText
      : '#ffffff',
    wordWrap: 'break-word',
    textAlign: 'left',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  });
  const userStyle = (isYou: boolean): React.CSSProperties => ({
    fontSize: '12px',
    opacity: isSkyxNew ? 1 : 0.7,
    marginBottom: '4px',
    color: isSkyxNew
      ? isYou
        ? SKYX_NEW_CHAT_CHROME.youLabelText
        : SKYX_NEW_CHAT_CHROME.otherUsernameText
      : undefined,
  });
  const timestampStyle: React.CSSProperties = {
    fontSize: '10px',
    opacity: isSkyxNew ? 1 : 0.5,
    marginTop: '4px',
    color: isSkyxNew ? SKYX_NEW_CHAT_CHROME.timestampText : undefined,
  };
  const inputAreaStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: chatCompactChrome ? '8px' : '10px',
    padding: chatCompactChrome ? '10px 12px' : '16px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    background: isSkyxNew
      ? SKYX_NEW_CHAT_CHROME.headerFooterBg
      : 'rgba(13, 17, 23, 0.7)',
    flexShrink: 0,
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box',
    ...(chatHandheldLandscape && isEmojiPickerOpen
      ? { position: 'relative', overflow: 'visible', zIndex: 2 }
      : {}),
  };
  const inputRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: isSkyxNew ? '8px' : undefined,
    minWidth: 0,
  };
  const inputStyle: React.CSSProperties = {
    flex: 1,
    background: isSkyxNew
      ? SKYX_NEW_CHAT_CHROME.messageInputBg
      : 'rgba(26, 35, 43, 0.8)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '8px',
    padding: '8px 12px',
    color: '#e8f0f8',
    outline: 'none',
  };
  const skyxInputWrapperStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    background: SKYX_NEW_CHAT_CHROME.messageInputBg,
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '8px',
    minWidth: 0,
    overflow: 'hidden',
    boxSizing: 'border-box',
  };
  const skyxEmojiButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: SKYX_NEW_CHAT_CHROME.accentText,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 0 0 10px',
    flexShrink: 0,
  };
  const skyxInputFieldStyle: React.CSSProperties = {
    flex: 1,
    background: 'transparent',
    border: 'none',
    padding: '8px 36px 8px 4px',
    color: '#e8f0f8',
    outline: 'none',
    boxShadow: 'none',
    minWidth: 0,
  };
  const skyxCharRemainingStyle: React.CSSProperties = {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '11px',
    lineHeight: 1,
    color: SKYX_NEW_CHAT_CHROME.timestampText,
    opacity: 0.85,
    userSelect: 'none',
    pointerEvents: 'none',
  };
  const charactersRemaining = CHAT_MESSAGE_MAX_LENGTH - inputText.length;
  const sendButtonStyle: React.CSSProperties = {
    background: isSkyxNew ? SKYX_NEW_CHAT_CHROME.sendButtonBg : '#22c55e',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: isSkyxNew ? '8px 10px' : '8px 16px',
    marginLeft: isSkyxNew ? 0 : '8px',
    cursor: 'pointer',
    fontWeight: '600',
    boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
    ...(isSkyxNew
      ? {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '40px',
          flexShrink: 0,
        }
      : {}),
  };
  const chatRulesButtonStyle: React.CSSProperties = {
    ...skyxNewChatHeaderLayoutStyles.iconButton,
    color: SKYX_NEW_CHAT_CHROME.accentText,
    transform: 'translateY(-2px)',
  };
  const errorStyle: React.CSSProperties = {
    padding: '8px 12px',
    margin: '0 16px 8px',
    background: 'rgba(239, 68, 68, 0.2)',
    border: '1px solid rgba(239, 68, 68, 0.5)',
    color: '#f87171',
    borderRadius: '6px',
    fontSize: '12px',
    textAlign: 'center',
  };
  const chatPopupOverlayStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    zIndex: 6,
  };
  const chatPopupStyle: React.CSSProperties = {
    pointerEvents: 'auto',
    background: '#271574',
    border: '1px solid rgba(158, 84, 227, 0.6)',
    borderRadius: '8px',
    padding: '12px 18px',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: 600,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
    textAlign: 'center',
    maxWidth: '85%',
  };
  const handheldLandscapeEmojiChrome = chatHandheldLandscape
    ? getSkyxNewHandheldLandscapeEmojiPickerChrome()
    : null;
  const emojiPickerStyle: React.CSSProperties = {
    width: '100%',
    flexShrink: 0,
    background: '#11043a',
    border: '1px solid rgba(158, 84, 227, 0.35)',
    borderRadius: '8px',
    padding: handheldLandscapeEmojiChrome
      ? `${handheldLandscapeEmojiChrome.panelPaddingPx}px`
      : '10px',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
    boxSizing: 'border-box',
    overflow: 'hidden',
    ...(chatHandheldLandscape
      ? getSkyxNewHandheldLandscapeEmojiPickerPositionStyle()
      : {}),
  };
  const emojiSearchStyle: React.CSSProperties = {
    width: '100%',
    background: '#271574',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    padding: chatHandheldLandscape ? '5px 8px' : '6px 10px',
    color: '#ffffff',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: chatHandheldLandscape ? '4px' : '6px',
  };
  const emojiGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: handheldLandscapeEmojiChrome
      ? `repeat(${handheldLandscapeEmojiChrome.gridColumns}, minmax(0, 1fr))`
      : 'repeat(8, minmax(0, 1fr))',
    gap: '2px',
    maxHeight: `${getSkyxNewEmojiPickerGridMaxHeightPx(deviceType, isLandscape)}px`,
    overflowY: 'auto',
  };
  const emojiOptionStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '18px',
    lineHeight: 1,
    padding: '4px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  const likeButtonColumnStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
    width: '26px',
    alignSelf: 'flex-end',
    marginBottom: '18px',
    gap: '2px',
  };
  const likeHeartSlotStyle: React.CSSProperties = {
    width: '22px',
    height: '22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };
  const likeButtonStyle = (liked: boolean): React.CSSProperties => ({
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    width: '22px',
    height: '22px',
    color: liked ? '#ff3b5c' : 'rgba(255,255,255,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
    boxShadow: 'none',
    WebkitTapHighlightColor: 'transparent',
    flexShrink: 0,
  });
  const likeCountStyle = (visible: boolean): React.CSSProperties => ({
    fontSize: '11px',
    fontWeight: 600,
    color: '#ff3b5c',
    lineHeight: '14px',
    minHeight: '14px',
    width: '100%',
    textAlign: 'center',
    visibility: visible ? 'visible' : 'hidden',
  });
  const localPlayerId = user?.playerId ?? null;
  const localNickname = user?.nickname?.trim() || '';
  const getSkyxAvatarUrl = (messageUser: string, isYou: boolean): string =>
    resolvePlayerAvatarUrl(
      isYou ? (localPlayerId ?? messageUser) : messageUser,
      localPlayerId,
      undefined,
      localNickname
    );
  const getSkyxAvatarFallbackLabel = (
    messageUser: string,
    isYou: boolean
  ): string =>
    (isYou ? user?.nickname || user?.username || messageUser : messageUser)
      .trim()
      .charAt(0)
      .toUpperCase() || '?';

  const renderMessageLikeButton = (message: ChatMessage) => (
    <div style={likeButtonColumnStyle}>
      <div style={likeHeartSlotStyle}>
        <button
          type="button"
          data-skyx-chat-like
          style={likeButtonStyle(message.likedByMe)}
          onClick={() => handleToggleLike(message)}
          onMouseDown={event => event.currentTarget.blur()}
          aria-label={
            message.likedByMe
              ? tSkyx('ui.chat.unlike_message')
              : tSkyx('ui.chat.like_message')
          }
        >
          <ChatHeartIcon filled={message.likedByMe} />
        </button>
      </div>
      <span
        style={likeCountStyle(message.likeCount > 0)}
        aria-hidden={message.likeCount === 0}
      >
        {message.likeCount > 0 ? message.likeCount : '0'}
      </span>
    </div>
  );

  const chatPanel = (
    <div
      ref={chatPanelRef}
      style={chatPanelStyle}
      data-skyx-new-ui
      data-skyx-chat-panel
    >
      <div style={headerStyle}>
        <div style={headerTitleGroupStyle}>
          {isSkyxNew && (
            <button
              type="button"
              data-skyx-chat-info={isSkyxNew ? true : undefined}
              style={chatRulesButtonStyle}
              onClick={() => setIsChatRulesOpen(true)}
              aria-label={tSkyx('ui.chat.rules.open')}
            >
              <ChatRulesMenuIcon />
            </button>
          )}
          <span
            style={isSkyxNew ? skyxNewChatHeaderLayoutStyles.title : undefined}
          >
            {t('ui.chat.title')}
          </span>
        </div>
        <div style={headerCloseGroupStyle}>
          {isSkyxNew ? (
            <SkyxNewModalCloseButton
              onClick={toggleChat}
              ariaLabel={t('ui.common.close')}
              mobile={deviceType !== 'desktop'}
              chatClose
            />
          ) : (
            <button
              type="button"
              style={closeButtonStyle}
              onClick={toggleChat}
              onMouseLeave={event => {
                event.currentTarget.blur();
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>
      <div style={messagesAreaContainerStyle}>
        <div
          ref={messagesAreaRef}
          style={messagesAreaStyle}
          onScroll={isSkyxNew ? updateScrollPositionState : undefined}
        >
          {messages.map(msg => {
            const isYou = isOwnChatMessage(msg, {
              user,
              sentClientSeqs: sentMessageIds.current,
            });
            const winShare = isSkyxNew
              ? parseSkyxWinShareMessage(msg.text)
              : null;

            return (
              <div key={msg.id} style={messageStyle(!!isYou, !!winShare)}>
                {winShare ? (
                  <SkyxNewChatWinCard
                    data={winShare}
                    date={formatShareDate(new Date(msg.timestampMs))}
                  />
                ) : isSkyxNew ? (
                  <div style={skyxMessageRowStyle(!!isYou)}>
                    {!isYou &&
                      (() => {
                        const avatarUrl = getSkyxAvatarUrl(msg.user, !!isYou);
                        return avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt=""
                            aria-hidden
                            style={skyxAvatarStyle}
                            key={`${msg.id}-${avatarRevision}-left`}
                          />
                        ) : (
                          <div
                            style={skyxAvatarFallbackStyle}
                            aria-hidden
                            key={`${msg.id}-${avatarRevision}-left-fallback`}
                          >
                            {getSkyxAvatarFallbackLabel(msg.user, !!isYou)}
                          </div>
                        );
                      })()}
                    {isYou && renderMessageLikeButton(msg)}
                    <div style={skyxMessageContentStyle(!!isYou)}>
                      <span style={userStyle(!!isYou)}>
                        {isYou
                          ? t('ui.chat.you')
                          : formatSkyxPlayerDisplayName(msg.user)}
                      </span>
                      <div style={messageBubbleStyle(!!isYou)}>{msg.text}</div>
                      <span style={timestampStyle}>
                        {formatChatMessageTime(msg.timestampMs)}
                      </span>
                    </div>
                    {!isYou && renderMessageLikeButton(msg)}
                    {isYou &&
                      (() => {
                        const avatarUrl = getSkyxAvatarUrl(msg.user, !!isYou);
                        return avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt=""
                            aria-hidden
                            style={skyxAvatarStyle}
                            key={`${msg.id}-${avatarRevision}-right`}
                          />
                        ) : (
                          <div
                            style={skyxAvatarFallbackStyle}
                            aria-hidden
                            key={`${msg.id}-${avatarRevision}-right-fallback`}
                          >
                            {getSkyxAvatarFallbackLabel(msg.user, !!isYou)}
                          </div>
                        );
                      })()}
                  </div>
                ) : (
                  <>
                    <span style={userStyle(!!isYou)}>
                      {isYou ? t('ui.chat.you') : msg.user}
                    </span>
                    <div style={messageBubbleStyle(!!isYou)}>{msg.text}</div>
                  </>
                )}
                {!isSkyxNew || !!winShare ? (
                  <span style={timestampStyle}>
                    {formatChatMessageTime(msg.timestampMs)}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
        {isSkyxNew && showNewMessageIndicator && (
          <button
            type="button"
            style={newMessageIndicatorStyle}
            onClick={handleNewMessageIndicatorClick}
          >
            {tSkyx('ui.chat.new_message')}
          </button>
        )}
      </div>
      {error && <div style={errorStyle}>{error}</div>}
      <div style={inputAreaStyle}>
        {isSkyxNew && isEmojiPickerOpen && (
          <div
            ref={emojiPickerRef}
            style={emojiPickerStyle}
            role="dialog"
            aria-label="Emoji picker"
          >
            <input
              type="text"
              data-skyx-emoji-search
              value={emojiSearch}
              onChange={e => setEmojiSearch(e.target.value)}
              style={emojiSearchStyle}
              placeholder={tSkyx('ui.chat.search_emoji')}
              aria-label={tSkyx('ui.chat.search_emoji')}
            />
            <div style={emojiGridStyle}>
              {filteredEmojis.map(item => (
                <button
                  key={`${item.emoji}-${item.name}`}
                  type="button"
                  style={emojiOptionStyle}
                  onClick={() => handleEmojiSelect(item.emoji)}
                  aria-label={item.emoji}
                >
                  {item.emoji}
                </button>
              ))}
            </div>
          </div>
        )}
        <div style={inputRowStyle} ref={composerRef}>
          {isSkyxNew ? (
            <div style={skyxInputWrapperStyle} data-skyx-chat-input-wrap>
              <button
                type="button"
                style={skyxEmojiButtonStyle}
                onClick={e => {
                  e.stopPropagation();
                  setIsEmojiPickerOpen(open => !open);
                }}
                aria-label="Open emoji picker"
              >
                <ChatEmojiIcon />
              </button>
              <input
                ref={chatInputRef}
                type="text"
                data-skyx-chat-input
                value={inputText}
                onChange={e => handleInputChange(e.target.value)}
                style={skyxInputFieldStyle}
                placeholder={t('ui.chat.type_message')}
                maxLength={CHAT_MESSAGE_MAX_LENGTH}
                onKeyDown={e => e.key === 'Enter' && handleSendMessage(e)}
              />
              <span style={skyxCharRemainingStyle} aria-live="polite">
                {charactersRemaining}
              </span>
            </div>
          ) : (
            <input
              type="text"
              value={inputText}
              onChange={e => handleInputChange(e.target.value)}
              style={inputStyle}
              placeholder={t('ui.chat.type_message')}
              maxLength={CHAT_MESSAGE_MAX_LENGTH}
              onKeyDown={e => e.key === 'Enter' && handleSendMessage(e)}
            />
          )}
          <button
            type="button"
            data-skyx-chat-send={isSkyxNew ? true : undefined}
            style={sendButtonStyle}
            onClick={e => handleSendMessage(e)}
            aria-label={t('ui.chat.send')}
          >
            {isSkyxNew ? <ChatSendIcon /> : t('ui.chat.send')}
          </button>
        </div>
      </div>
      {isSkyxNew && (
        <SkyxNewChatRulesModal
          isOpen={isChatRulesOpen}
          onClose={() => setIsChatRulesOpen(false)}
        />
      )}
      {isSkyxNew && chatPopupMessage && (
        <div style={chatPopupOverlayStyle} role="status" aria-live="polite">
          <div style={chatPopupStyle}>{chatPopupMessage}</div>
        </div>
      )}
    </div>
  );

  if (isSkyxNew && typeof document !== 'undefined') {
    return createPortal(chatPanel, chatPortalRoot ?? document.body);
  }

  return chatPanel;
};
